/**
 * The chrome every authenticated view sits in: billing state, environment,
 * identity, navigation, sign-out.
 *
 * DESIGN SPIKE (design/tailwind-shell). This file is a REPRESENTATION rewrite
 * of the previous header-strip chrome into the 260px sidebar shell the owner
 * picked: a switcher block, grouped nav with uppercase headings, collapsible
 * children on a vertical guide line, a bottom-pinned identity block above a
 * hairline, a topbar with the collapse toggle and a breadcrumb.
 *
 * IT STILL DECIDES NOTHING ITSELF, and that is the part the rewrite could not
 * touch. Whether the owner-only tab renders is `isAllowed('adminUsers', ctx)`'s
 * answer; what the billing banner says is `billingBanner` + `billingBannerText`;
 * which fragment addresses which sub-tab is `staffTabHref`/`masterDataTabHref`;
 * whether a click may be intercepted is `isPlainLeftClick`. Re-implementing any
 * of them here would produce a second, quieter copy of the decision table --
 * the exact failure mode Task 5 exists to prevent. And none of it is
 * authorization: the server refuses what this hides (admin spec 4.2).
 *
 * ONE DELIBERATE SIMPLIFICATION vs. the old file: the owner-only entry's href is
 * now `staffTabHref('adminUsers')` instead of a local `ADMIN_USERS_HREF`
 * constant that had to stay byte-identical to lib/router.ts's fragment table by
 * hand. Same string, one source.
 *
 * WHAT THE SWITCHER BLOCK IS NOT: the reference component put a workspace picker
 * there. This account has one tenant and no switching, so the block is a
 * non-interactive identity card -- the tenant NAME (never the id: every rendered
 * internal id eventually becomes a support conversation about it), its plan and
 * a health dot. A control that implies switching we cannot do would be a lie
 * with a chevron on it.
 *
 * PRESENTATIONAL ON PURPOSE, unchanged: `route`, `hash`, `onNavigate` and
 * `onLogout` arrive as props instead of being read from the Router and the
 * QueryClient. That is what lets DOM suite 1 render the real component with
 * nothing but a `me` record.
 */
import { useState } from 'react';
import type { ComponentType, MouseEvent, ReactNode } from 'react';
import {
  Activity, Boxes, ChevronRight, LayoutDashboard, LogOut, Moon, PanelLeftClose,
  PanelLeftOpen, ShieldCheck, Store, Sun, Table2, Users,
} from 'lucide-react';
import { billingBanner, isAllowed, type CapabilityCtx } from './lib/capabilities';
import { billingBannerText } from './lib/billingCopy';
import {
  isPlainLeftClick, masterDataTabHref, parseMasterDataTab, parseStaffTab, pathFor, staffTabHref,
  type MasterDataTab, type RouteId, type StaffTab,
} from './lib/router';

import { t } from './lib/i18n';
import { cn } from './lib/utils';

import type { MeResponse } from './lib/types';

/**
 * The six views of admin spec 4.3. Derived from `RouteId` rather than written
 * out again, so a seventh route added to lib/router.ts's table is a TypeScript
 * error in `navLabel` and `NAV_ICON` until it has been given a label and an
 * icon -- a nav that silently omits a route is the failure this shape prevents.
 */
type MainNavId = Exclude<RouteId, 'login' | 'notFound'>;

/** Literal t() calls, one per entry: see the note in components/Blocking.tsx. */
function navLabel(id: MainNavId): string {
  switch (id) {
    case 'start': return t('nav.start');
    case 'staff': return t('nav.staff');
    case 'sheet': return t('nav.sheet');
    case 'masterData': return t('nav.masterData');
    case 'ebay': return t('nav.ebay');
    case 'jobs': return t('nav.jobs');
  }
}

type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

const NAV_ICON: Record<MainNavId, IconType> = {
  start: LayoutDashboard,
  staff: Users,
  sheet: Table2,
  masterData: Boxes,
  ebay: Store,
  jobs: Activity,
};

/**
 * The grouping is PRESENTATION ONLY -- six routes in the spec's order, cut into
 * four labelled runs so the rail reads as sections rather than as a list. No
 * route gains or loses visibility by being in a group.
 */
const NAV_GROUPS: readonly { heading: () => string; items: readonly MainNavId[] }[] = [
  { heading: () => t('nav.group.overview'), items: ['start'] },
  { heading: () => t('nav.group.manage'), items: ['staff', 'masterData'] },
  { heading: () => t('nav.group.connections'), items: ['sheet', 'ebay'] },
  { heading: () => t('nav.group.operations'), items: ['jobs'] },
];

/** The breadcrumb's first segment. `null` for a route no group claims. */
function groupHeadingFor(route: MainNavId): string | null {
  const group = NAV_GROUPS.find((candidate) => candidate.items.includes(route));
  return group ? group.heading() : null;
}

/** Literal t() calls, one per sub-tab, for the same reason `navLabel` is one. */
function staffChildLabel(tab: StaffTab): string {
  switch (tab) {
    case 'staff': return t('staff.tab.staff');
    case 'devices': return t('staff.tab.devices');
    case 'adminUsers': return t('nav.adminUsers');
  }
}

function masterDataChildLabel(tab: MasterDataTab): string {
  switch (tab) {
    case 'locations': return t('masterData.tab.locations');
    case 'lists': return t('masterData.tab.lists');
    case 'settings': return t('masterData.tab.settings');
  }
}

interface ChildEntry {
  key: string;
  label: string;
  href: string;
  /** Rendered after the label; the reference component's badge slot. */
  badge?: string;
  active: boolean;
}

export interface LayoutProps {
  me: MeResponse;
  /** From /admin-config.json. Anything but 'production' is labelled. */
  buildEnv: string;
  /** The active route, for aria-current. Absent = no entry is current. */
  route?: RouteId;
  /** The raw fragment, for the sub-entries' own aria-current. Absent = none. */
  hash?: string;
  /** Absent = the anchors navigate the browser, which is still correct. */
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  children: ReactNode;
}

export function Layout({ me, buildEnv, route, hash, onNavigate, onLogout, children }: LayoutProps) {
  const ctx: CapabilityCtx = { role: me.role, status: me.tenant.status, features: me.features };
  const showAdminUsers = isAllowed('adminUsers', ctx);
  const banner = billingBannerText(billingBanner(me.tenant, Date.now()));

  const [collapsed, setCollapsed] = useState(false);
  /**
   * SESSION-ONLY, and it has to be. eslint.config.js bans every client-side
   * persistence path on this surface, so a remembered choice has nowhere legal
   * to live; `null` means "whatever the operating system says", which is the
   * state the owner's dark desktop already lands on without touching anything.
   */
  const [theme, setTheme] = useState<'dark' | 'light' | null>(null);
  /**
   * Both parents start EXPANDED. The children are real navigation, not a
   * disclosure: an owner must be able to see that an Administratoren section
   * exists without first guessing that Mitarbeiter has one.
   */
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    staff: true,
    masterData: true,
  });

  function applyTheme(next: 'dark' | 'light') {
    setTheme(next);
    const root = document.documentElement;
    root.classList.toggle('dark', next === 'dark');
    root.classList.toggle('light', next === 'light');
  }

  function handleNav(event: MouseEvent<HTMLAnchorElement>, path: string) {
    // A real href, intercepted only for a plain left click: middle-click,
    // cmd-click, shift-click and alt-click must keep doing what the browser
    // does with them (lib/router.ts's `isPlainLeftClick`, which is where that
    // list and the reason for each entry lives), and without a router (DOM
    // suite 1) the anchor stays a plain link.
    if (!onNavigate) return;
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onNavigate(path);
  }

  /** The fragment is parsed by lib/router.ts's pure parsers, never here. */
  function childrenFor(id: MainNavId): ChildEntry[] {
    if (id === 'staff') {
      const active = route === 'staff' ? parseStaffTab(hash ?? '') : null;
      const tabs: StaffTab[] = showAdminUsers ? ['staff', 'devices', 'adminUsers'] : ['staff', 'devices'];
      return tabs.map((tab) => ({
        key: tab,
        label: staffChildLabel(tab),
        href: staffTabHref(tab),
        // The owner-only entry says so. "an owner can manage admin users" is a
        // fact about the account a manager must not learn from a nav item --
        // which is why the entry is absent above, not merely labelled here.
        ...(tab === 'adminUsers' ? { badge: t('nav.ownerOnly') } : {}),
        active: active === tab,
      }));
    }
    if (id === 'masterData') {
      const active = route === 'masterData' ? parseMasterDataTab(hash ?? '') : null;
      return (['locations', 'lists', 'settings'] as const).map((tab) => ({
        key: tab,
        label: masterDataChildLabel(tab),
        href: masterDataTabHref(tab),
        active: active === tab,
      }));
    }
    return [];
  }

  return (
    <div className="shell flex min-h-screen flex-col bg-bg text-text md:flex-row">
      {/* ── the rail ─────────────────────────────────────────────────────
          Always rendered, never hidden by a breakpoint: on a narrow screen it
          becomes a bounded, scrollable strip above the content rather than
          disappearing, because a shell whose navigation exists only above
          768px is a shell with no navigation on the device it broke on. */}
      <aside
        className={cn(
          'flex max-h-[45vh] w-full shrink-0 flex-col border-b border-line bg-bg-subtle',
          'md:sticky md:top-0 md:max-h-none md:h-screen md:border-b-0 md:border-r',
          collapsed ? 'md:w-[68px]' : 'md:w-[260px]',
        )}
      >
        {/* The switcher block. Non-interactive by design -- see the file note. */}
        <div className={cn('flex items-center gap-2.5 border-b border-line px-3.5 py-3.5', collapsed && 'justify-center px-0')}>
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-[0.5rem] bg-accent text-[0.8125rem] font-semibold text-accent-fg shadow-card"
          >
            {me.tenant.name.trim().slice(0, 1).toUpperCase()}
          </span>
          {collapsed ? null : (
            <span className="min-w-0 flex-1">
              {/* The tenant NAME, never the tenant id. */}
              <strong className="tenant-name block truncate text-[0.8125rem] font-semibold leading-tight">
                {me.tenant.name}
              </strong>
              <span className="mt-0.5 flex items-center gap-1.5">
                <span className="truncate text-[0.6875rem] uppercase tracking-[0.06em] text-muted">
                  {me.tenant.plan}
                </span>
                {/* A DOT, NOT A STATUS PILL, and the reason is not taste.
                    `tenantStatusPill` is an exhaustive switch over the
                    five-literal union with no default, and api.ts casts the
                    response body with no runtime validation -- so a status
                    outside the union (which capabilities.ts and billingBanner
                    both handle on purpose, as `unknown`) makes that function
                    return undefined and would take the whole shell down. This
                    reads the one TOTAL answer instead: `banner === null` is
                    exactly "status is active", so the dot says whether anything
                    needs attention and the banner below says what. */}
                <span
                  aria-hidden="true"
                  title={banner ?? undefined}
                  className={cn('size-1.5 shrink-0 rounded-full', banner === null ? 'bg-ok' : 'bg-warn')}
                />
              </span>
            </span>
          )}
        </div>

        <nav aria-label={t('nav.label')} className="flex-1 overflow-y-auto px-2.5 py-3">
          {NAV_GROUPS.map((group) => (
            <div key={group.heading()} className="mb-4 last:mb-0">
              {collapsed ? (
                <div aria-hidden="true" className="mx-2 mb-2 border-t border-line-soft" />
              ) : (
                <h2 className="px-2 pb-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.09em] text-faint">
                  {group.heading()}
                </h2>
              )}
              <ul className="list-none space-y-0.5 p-0">
                {group.items.map((id) => {
                  const Icon = NAV_ICON[id];
                  const kids = childrenFor(id);
                  const open = openSections[id] ?? true;
                  const current = route === id;
                  return (
                    <li key={id}>
                      <div className="flex items-center gap-1">
                        <a
                          href={pathFor(id)}
                          aria-current={current ? 'page' : undefined}
                          onClick={(event) => handleNav(event, pathFor(id))}
                          title={collapsed ? navLabel(id) : undefined}
                          className={cn(
                            'group flex min-w-0 flex-1 items-center gap-2.5 rounded-field px-2 py-[0.4375rem]',
                            'text-[0.8125rem] font-medium no-underline transition-colors',
                            collapsed && 'justify-center px-0',
                            current
                              ? 'bg-accent-soft text-text hover:no-underline'
                              : 'text-muted hover:bg-surface-2 hover:text-text hover:no-underline',
                          )}
                        >
                          <Icon
                            aria-hidden="true"
                            className={cn('size-[1.0625rem] shrink-0', current ? 'text-accent' : 'text-faint group-hover:text-muted')}
                          />
                          <span className={cn('truncate', collapsed && 'sr-only')}>{navLabel(id)}</span>
                        </a>
                        {kids.length === 0 || collapsed ? null : (
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-label={open
                              ? t('nav.section.collapse', { section: navLabel(id) })
                              : t('nav.section.expand', { section: navLabel(id) })}
                            onClick={() => setOpenSections((prev) => ({ ...prev, [id]: !open }))}
                            className="grid size-6 shrink-0 place-items-center rounded-field border-0 bg-transparent p-0 text-faint hover:bg-surface-2 hover:text-text"
                          >
                            <ChevronRight
                              aria-hidden="true"
                              className={cn('size-3.5 transition-transform', open && 'rotate-90')}
                            />
                          </button>
                        )}
                      </div>

                      {/* The collapsible children, on the vertical guide line. */}
                      {kids.length === 0 || collapsed || !open ? null : (
                        <ul className="mt-0.5 list-none space-y-0.5 border-l border-line p-0 pl-0 ml-[1.1875rem]">
                          {kids.map((child) => (
                            <li key={child.key}>
                              <a
                                href={child.href}
                                aria-current={child.active ? 'page' : undefined}
                                onClick={(event) => handleNav(event, child.href)}
                                className={cn(
                                  'flex items-center gap-2 rounded-r-field py-[0.3125rem] pl-3.5 pr-2',
                                  'text-[0.8125rem] no-underline transition-colors hover:no-underline',
                                  child.active
                                    ? 'font-medium text-text shadow-[inset_2px_0_0_var(--accent)]'
                                    : 'text-muted hover:text-text',
                                )}
                              >
                                <span className="min-w-0 flex-1 truncate">{child.label}</span>
                                {child.badge === undefined ? null : (
                                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-line bg-surface-2 px-1.5 py-px text-[0.625rem] font-medium text-muted">
                                    <ShieldCheck aria-hidden="true" className="size-2.5" />
                                    {child.badge}
                                  </span>
                                )}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* The bottom-pinned block, above a hairline. */}
        <div className={cn('border-t border-line px-2.5 py-2.5', collapsed && 'px-1.5')}>
          {collapsed ? null : (
            <p className="signed-in mb-1.5 truncate px-2 text-[0.6875rem] text-muted" title={me.user.email}>
              {t('layout.signedInAs', { email: me.user.email })}
            </p>
          )}
          <button
            type="button"
            className={cn(
              'sign-out flex w-full items-center gap-2.5 rounded-field border-0 bg-transparent px-2 py-[0.4375rem]',
              'text-[0.8125rem] font-medium text-muted hover:bg-surface-2 hover:text-text',
              collapsed && 'justify-center px-0',
            )}
            title={collapsed ? t('nav.signOut') : undefined}
            onClick={onLogout}
          >
            <LogOut aria-hidden="true" className="size-[1.0625rem] shrink-0" />
            <span className={cn(collapsed && 'sr-only')}>{t('nav.signOut')}</span>
          </button>
        </div>
      </aside>

      {/* ── the content column ───────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="shell-header sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-bg/85 px-4 backdrop-blur-md md:px-6">
          <button
            type="button"
            aria-label={collapsed ? t('nav.railExpand') : t('nav.railCollapse')}
            onClick={() => setCollapsed((prev) => !prev)}
            className="hidden size-8 shrink-0 place-items-center rounded-field border-0 bg-transparent p-0 text-muted hover:bg-surface-2 hover:text-text md:grid"
          >
            {collapsed
              ? <PanelLeftOpen aria-hidden="true" className="size-[1.0625rem]" />
              : <PanelLeftClose aria-hidden="true" className="size-[1.0625rem]" />}
          </button>

          {/* A breadcrumb of PLAIN TEXT, not links, and it names the RAIL GROUP
              rather than the tenant: there is no hierarchy above a view to
              navigate back to, a second anchor carrying a nav item's own label
              would be two controls with one name, and repeating the tenant name
              the switcher block already shows makes it ambiguous on screen. */}
          <nav aria-label={t('layout.breadcrumb')} className="flex min-w-0 items-center gap-1.5 text-[0.8125rem]">
            {route === undefined || route === 'notFound' || route === 'login' ? (
              <span className="truncate text-muted">{t('nav.label')}</span>
            ) : (
              <>
                <span className="truncate text-muted">{groupHeadingFor(route)}</span>
                <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
                <span className="truncate font-medium text-text">{navLabel(route)}</span>
              </>
            )}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {buildEnv === 'production' ? null : (
              <span className="env-chip inline-flex items-center rounded-full border border-warn/40 bg-warn-soft px-2 py-0.5 text-[0.6875rem] font-medium text-warn">
                {t('env.chip', { env: buildEnv })}
              </span>
            )}
            <button
              type="button"
              aria-label={t('layout.themeToggle')}
              title={t('layout.themeToggle')}
              onClick={() => applyTheme(theme === 'dark' ? 'light' : 'dark')}
              className="grid size-8 place-items-center rounded-field border-0 bg-transparent p-0 text-muted hover:bg-surface-2 hover:text-text"
            >
              {theme === 'dark'
                ? <Sun aria-hidden="true" className="size-[1.0625rem]" />
                : <Moon aria-hidden="true" className="size-[1.0625rem]" />}
            </button>
          </div>
        </header>

        {/* Above the content on purpose (Merker e): billing state is the first
            thing on the screen, not a line in a settings tab. */}
        {banner === null ? null : (
          <p
            className="billing-banner flex items-center gap-2 border-b border-warn/35 bg-warn-soft px-4 py-2 text-[0.8125rem] font-medium text-warn md:px-6"
            role="status"
          >
            {banner}
          </p>
        )}

        <main className="shell-main mx-auto w-full max-w-[1180px] flex-1 px-4 py-6 md:px-6 md:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
